import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Table,
  Button,
  TableBody,
  Container,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Link, Link as RouterLink } from 'react-router-dom';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import Scrollbar from '../../components/Scrollbar';
import Label from '../../components/Label';
import Searchbar from '../../components/Searchbar';
import { useAppDispatch, useAppSelector } from '../../store';
import { findCategoryCake } from '../../store/categoryCake/api';
import {
  setCurrentPage,
  setLimit,
  setSkip,
} from '../../store/categoryCake/categoryCakeSlice';
import Loader from '../../components/Loader';
import {
  reset as resetCategoryCake,
  setStatus as setCategoryCakeStatus,
} from '../../store/categoryCake/categoryCakeSlice';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { encryptString } from '../../utils/url';

const Index = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.categoryCake);
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findCategoryCake({
          limit,
          skip,
          ...(search && { search: search }),
        })
      );
    }
  }, [dispatch, limit, skip, status]);

  useEffect(() => {
    dispatch(resetCategoryCake());
  }, [search]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setCategoryCakeStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  return (
    <Page title={'Occasion Cake: List'}>
      <Container maxWidth={'xl'}>
        <HeaderBreadcrumbs
          heading={'Occasion Cake'}
          links={[{ name: 'Dashboard', href: '/' }]}
          action={
            <Button
              variant='contained'
              component={RouterLink}
              to={'/occasion-cake/create'}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
            >
              {'New Occasion Cake'}
            </Button>
          }
        />

        <Card>
          <Searchbar handleSearch={handleSearch} />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
              <Table size={'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>{'Label EN'}</TableCell>
                    <TableCell align='center'>{'Label AR'}</TableCell>
                    <TableCell align='center'>{'Occasion'}</TableCell>
                    <TableCell align='center'>{'Position'}</TableCell>
                    <TableCell align='center'>{'Status'}</TableCell>
                    <TableCell align='center'>{'Action'}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow hover>
                      <TableCell
                        colSpan={8}
                        align='center'
                      >
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align='center'
                      >
                        {'No record found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.map((categoryCake) => (
                      <TableRow key={categoryCake.id}>
                        <TableCell align='center'>
                          {categoryCake?.labelEn}
                        </TableCell>
                        <TableCell align='center'>
                          {categoryCake?.labelAr}
                        </TableCell>
                        <TableCell align='center'>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {categoryCake?.categoryCount && categoryCake.categoryCount > 1 ? (
                                <Label variant='outlined' color='default'>
                                  {categoryCake.categoryCount} Occasions
                                </Label>
                              ) : (
                                <Label variant='outlined' color='default'>
                                  1 Occasion
                                </Label>
                              )}
                              
                              <Tooltip
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      backgroundColor: '#E9DDD7',
                                      border: '1px solid #e9ecef',
                                      borderRadius: 2,
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                    }
                                  },
                                  arrow: {
                                    sx: {
                                      color: '#E9DDD7',
                                      '&::before': {
                                        backgroundColor: '#E9DDD7',
                                        border: '1px solid #e9ecef'
                                      }
                                    }
                                  }
                                }}
                                title={
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1 }}>
                                    {/* Primary Category */}
                                    {categoryCake?.category && (
                                      <Box sx={{ 
                                        backgroundColor: '#EE3750', 
                                        border: 1, 
                                        borderColor: '#bdbdbd',
                                        borderRadius: 1, 
                                        padding: '4px 8px', 
                                        fontSize: '0.75rem',
                                        color: 'white',
                                        fontWeight: 'bold'
                                      }}>
                                        {categoryCake.category.labelEn}
                                      </Box>
                                    )}
                                    {/* Additional Categories */}
                                    {categoryCake?.additionalCategories?.map((additionalCategory: any, index: number) => (
                                      <Box key={index} sx={{ 
                                        backgroundColor: '#EE3750', 
                                        border: 1, 
                                        borderColor: '#bdbdbd',
                                        borderRadius: 1, 
                                        padding: '4px 8px', 
                                        fontSize: '0.75rem',
                                        color: 'white'
                                      }}>
                                        {additionalCategory.category?.labelEn || `Additional Category ${index + 1}`}
                                      </Box>
                                    ))}
                                  </Box>
                                }
                                arrow
                                placement="top"
                              >
                                                                  <IconButton size="small" sx={{ color: '#808080' }}>
                                    <Iconify icon={'eva:info-fill'} width={16} height={16} />
                                  </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align='center'>
                          {categoryCake?.position}
                        </TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={
                              categoryCake.activeState ? 'success' : 'error'
                            }
                          >
                            {categoryCake.activeState ? 'Active' : 'InActive'}
                          </Label>
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton aria-label='visibility'>
                            <Link
                              to={`/occasion-cake/edit/${encryptString(
                                categoryCake?.id ?? ''
                              )}`}
                              state={{ ...categoryCake }}
                              style={{ color: '#808080' }}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </Link>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(Math.round(totalRecords / limit), currentPage)}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>
      </Container>
    </Page>
  );
};

export default Index;
